import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useCountUp } from "@/lib/useCountUp";
import { Sparkles } from "lucide-react";

// ---------- Holographic Prism Card — sapphire→violet 3D glass with laser sweep ----------
export function HolographicPrism({ stats }) {
  const gross = Number(stats?.total_spent || 0);
  const deducted = Number(stats?.deductible || 0);
  const savings = gross > 0 ? (deducted * 0.22) : 0; // rough marginal-bracket estimate
  // Odometer spin — roll the savings digits up dramatically on every stats change.
  // 1600ms feels celebratory but not slow; the same ease as the arc curves.
  const savingsCents = Math.round(savings * 100);
  const rolled = useCountUp(savingsCents, 1600);
  const rolledDollars = (rolled / 100).toFixed(2);
  const [whole, cents] = rolledDollars.split(".");
  return (
    <div
      data-testid="holographic-prism"
      className="holo-prism relative rounded-2xl p-6 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #1D4ED8 0%, #4338CA 40%, #6D28D9 70%, #7C3AED 100%)",
        border: "1px solid rgba(255,255,255,0.15)",
        transform: "perspective(900px) rotateX(3deg) rotateY(-2deg)",
        transformStyle: "preserve-3d",
        boxShadow:
          "0 30px 60px -20px rgba(29,78,216,0.55), 0 0 0 1px rgba(124,58,237,0.4) inset",
        minHeight: 168,
      }}
    >
      {/* Prismatic shimmer overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 15%, rgba(255,255,255,0.28), transparent 45%), " +
            "radial-gradient(circle at 80% 85%, rgba(124,58,237,0.6), transparent 55%)",
          mixBlendMode: "screen",
        }}
      />
      {/* White-hot laser sweep — fires once on mount, clip-path keeps it inside the card */}
      <span
        data-testid="holo-prism-laser"
        aria-hidden="true"
        className="holo-prism-laser absolute inset-0 pointer-events-none"
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-violet-100/90 flex items-center gap-1.5">
            <Sparkles size={11} /> Holographic Prism
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-violet-200/70 px-2 py-1 rounded-full"
               style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
            Projected tax savings
          </div>
        </div>
        <div
          data-testid="holo-prism-odometer"
          className="holo-odometer font-mono font-bold text-white leading-none flex items-baseline"
          style={{ fontSize: 40, textShadow: "0 0 24px rgba(255,255,255,0.35)" }}
        >
          <span className="text-white/70 text-2xl mr-1">$</span>
          {whole.split("").map((d, i) => (
            <span
              key={`${i}-${d}`}
              className="odo-digit inline-block tabular-nums"
              style={{ minWidth: "0.62em", textAlign: "center" }}
            >
              {d}
            </span>
          ))}
          <span className="text-white/70 mx-1">.</span>
          {cents.split("").map((d, i) => (
            <span
              key={`c-${i}-${d}`}
              className="odo-digit inline-block tabular-nums text-white/85"
              style={{ minWidth: "0.62em", textAlign: "center", fontSize: 28 }}
            >
              {d}
            </span>
          ))}
        </div>
        <div className="text-violet-100/85 text-xs mt-2">
          Based on <span className="font-mono text-white">${deducted.toFixed(2)}</span> of
          deductible spend at a 22% marginal bracket estimate.
        </div>
        <div className="mt-4 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-violet-200/80">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#a78bfa", boxShadow: "0 0 6px #a78bfa" }} />
            live
          </span>
          <span>·</span>
          <span>syncs with escape ring</span>
        </div>
      </div>
      <style>{`
        .holo-prism { transition: transform 400ms cubic-bezier(0.22,1,0.36,1); }
        .holo-prism:hover { transform: perspective(900px) rotateX(1deg) rotateY(0deg) translateY(-2px); }
        .holo-prism-laser {
          -webkit-clip-path: inset(0 round 16px);
          clip-path: inset(0 round 16px);
          background: linear-gradient(
            100deg,
            transparent 0%, transparent 38%,
            rgba(255,255,255,0.4) 46%,
            rgba(255,255,255,0.95) 50%,
            rgba(255,255,255,0.4) 54%,
            transparent 62%, transparent 100%
          );
          mix-blend-mode: screen;
          filter: blur(0.5px);
          transform: translateX(-120%);
          animation: holo-prism-laser-sweep 1600ms cubic-bezier(0.22, 1, 0.36, 1) 200ms 1;
        }
        @keyframes holo-prism-laser-sweep {
          0%   { transform: translateX(-120%); opacity: 0; }
          15%  { opacity: 1; }
          75%  { opacity: 1; }
          100% { transform: translateX(120%);  opacity: 0; }
        }
        /* Digit-flip animation — each character re-mounts on value change (key includes digit),
           and this animation runs on mount so digits appear to roll upward into place. */
        .odo-digit {
          animation: odo-roll 420ms cubic-bezier(0.22, 1, 0.36, 1);
          animation-fill-mode: both;
        }
        @keyframes odo-roll {
          0%   { transform: translateY(0.5em); opacity: 0; filter: blur(2px); }
          60%  { transform: translateY(-0.05em); opacity: 1; filter: blur(0); }
          100% { transform: translateY(0); opacity: 1; filter: blur(0); }
        }
      `}</style>
    </div>
  );
}
